import { jsxs, jsx } from "react/jsx-runtime";
import { cn } from "../../../lib/utils.js";
import "../shared/quiz-blank.js";
import { KatexText } from "../shared/katex-text.js";
import { QuizResultFeedback } from "../shared/quiz-result-feedback.js";
import { SubmitButton } from "../shared/submit-button.js";
import { QuizImage } from "../shared/quiz-image.js";
import "react";
import { useTranslation } from "react-i18next";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import { QuizMedia } from "../shared/quiz-media.js";
import { QuizSolveDescription } from "../shared/quiz-solve-description.js";
import { DifficultyBadge, DebugQuizId } from "../shared/debug-quiz-id.js";
import { formatText } from "../../../utils/quiz-parser.js";
import { useMultiChoiceQuiz } from "../hooks/use-multi-choice-quiz.js";
import { motion } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/render/components/motion/proxy.js";
function MultiChoiceStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  showAnswerCount = false,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel
}) {
  const { t } = useTranslation("quiz");
  const {
    selectedKeys,
    sortedAnswers,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    answerCount,
    resolvedCorrectKeySet,
    correctsBySelectedKey,
    gridContainerId,
    handleSelect,
    handleSubmit
  } = useMultiChoiceQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange
  });
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    quiz.subtype === "C" && /* @__PURE__ */ jsx("style", { dangerouslySetInnerHTML: {
      __html: `
            @media (min-width: 768px) {
              #${gridContainerId} {
                grid-template-columns: repeat(${sortedAnswers.length}, minmax(0, 1fr));
              }
            }
          `
    } }),
    /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0, y: -10 },
        animate: { opacity: 1, y: 0 },
        className: "space-y-2",
        children: [
          /* @__PURE__ */ jsxs("div", { className: "inline-flex gap-2 items-center", children: [
            /* @__PURE__ */ jsxs("div", { className: "inline-flex gap-2 items-center px-3 py-1 text-sm font-bold text-white bg-[#55C3FF] rounded-lg", children: [
              /* @__PURE__ */ jsx("span", { children: t("types.multiChoice.label") }),
              showAnswerCount && answerCount > 1 && /* @__PURE__ */ jsx("span", { className: "px-1.5 py-0.5 bg-white/20 rounded-xs text-[10px]", children: t("types.multiChoice.selectCount", { count: answerCount }) })
            ] }),
            /* @__PURE__ */ jsx(DifficultyBadge, { level: difficultyLevel }),
            /* @__PURE__ */ jsx(DebugQuizId, { quizId })
          ] }),
          /* @__PURE__ */ jsx("h3", { className: "text-2xl font-bold leading-relaxed text-gray-900", children: /* @__PURE__ */ jsx(KatexText, { text: quiz.title }) }),
          quiz.problem && /* @__PURE__ */ jsx("div", { className: "text-xl leading-relaxed text-gray-900", children: formatText(quiz.problem, `${quizId}-problem`, quiz.additionalImages ?? void 0) }),
          /* @__PURE__ */ jsx(QuizMedia, { image: quiz.image, imageMobile: quiz.imageMobile, desmos: quiz.desmos, vars: quiz.vars })
        ]
      }
    ),
    !isSolved && !isViewSolved && showAnswerCount && /* @__PURE__ */ jsx("p", { className: "text-lg text-gray-500", children: answerCount > 1 ? t("types.multiChoice.selectInstruction", { count: answerCount, selected: selectedKeys.length }) : t("types.multiChoice.selectOne") }),
    /* @__PURE__ */ jsx(
      "ul",
      {
        id: quiz.subtype === "C" ? gridContainerId : void 0,
        role: "listbox",
        "aria-label": t("types.multiChoice.selectOption"),
        "aria-multiselectable": answerCount > 1,
        className: cn(
          "grid gap-4 mt-6",
          quiz.subtype === "B" && "grid-cols-1 md:grid-cols-2",
          quiz.subtype === "C" && "grid-cols-1",
          (!quiz.subtype || quiz.subtype !== "B" && quiz.subtype !== "C") && "grid-cols-1"
        ),
        children: sortedAnswers.map((answer, idx) => {
          const isSelected = selectedKeys.includes(answer.key);
          const isInResolvedCorrectAnswers = resolvedCorrectKeySet.has(answer.key);
          const isCorrectByCorrects = correctsBySelectedKey.get(answer.key);
          const showAsCorrect = isSolved && (shouldShowCorrectAnswers ? isInResolvedCorrectAnswers : isSelected && (isCorrectByCorrects !== void 0 ? isCorrectByCorrects : isInResolvedCorrectAnswers));
          const isWrongSelected = isSolved && !shouldShowCorrectAnswers && isSelected && !showAsCorrect;
          const isCompleted = isSolved || isViewSolved;
          const hasImage = !!answer.image;
          return /* @__PURE__ */ jsxs(
            "li",
            {
              role: "option",
              "aria-selected": isSelected,
              onClick: () => !isCompleted && handleSelect(answer.key),
              className: cn(
                "group w-full p-3 rounded-2xl border-2 text-[20px] font-medium text-left transition-all",
                "flex items-center cursor-pointer",
                // text-only height
                !hasImage && "h-14",
                // 기본 상태
                !isSolved && !isSelected && "text-gray-900 border-[#BBBBBB] [box-shadow:0_4px_0_rgba(0,0,0,0.1)]",
                !isSolved && !isSelected && "hover:text-[#55C3FF] hover:border-[#55C3FF] hover:[box-shadow:0_4px_0_rgba(85,195,255,0.3)]",
                // 선택 상태 (풀이 전)
                !isSolved && isSelected && "text-[#2E96CE] bg-[#DDF3FF] border-[#55C3FF] [box-shadow:0_4px_0_rgba(0,0,0,0.1)]",
                // 정답
                showAsCorrect && "text-[#3BD0AE] bg-[#E2F8F3] border-[#3BD0AE] [box-shadow:unset]",
                // 오답
                isWrongSelected && "text-[#FF675E] bg-[#FFF0EF] border-[#FF675E] [box-shadow:unset]",
                // 비활성화 (풀이 후 미선택/미정답)
                isSolved && !showAsCorrect && !isWrongSelected && "border-[#BBBBBB] text-gray-900 opacity-60",
                // 완료 상태
                isCompleted && "pointer-events-none"
              ),
              children: [
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: cn(
                      "w-8 h-8 mr-4 font-bold rounded-full shrink-0 text-[16px] flex items-center justify-center transition-all",
                      // 기본 상태
                      !isSelected && !isSolved && "text-[#AAAAAA] bg-[#F0F0F0]",
                      !isSelected && !isSolved && "group-hover:text-[#55C3FF] group-hover:bg-[#DDF3FF]",
                      // 선택 상태 (풀이 전)
                      isSelected && !isSolved && "text-white bg-[#55C3FF]",
                      // 정답
                      showAsCorrect && "text-white bg-[#3BD0AE]",
                      // 오답
                      isWrongSelected && "text-white bg-[#FF675E]",
                      // 비활성화
                      isSolved && !showAsCorrect && !isWrongSelected && "text-[#AAAAAA] bg-[#F0F0F0]"
                    ),
                    children: idx + 1
                  }
                ),
                answer.image && /* @__PURE__ */ jsx(
                  QuizImage,
                  {
                    image: answer.image,
                    alt: t("types.multiChoice.optionAlt", { number: idx + 1 }),
                    className: "my-0 shrink-0"
                  }
                ),
                /* @__PURE__ */ jsx("div", { className: "flex-1 text-[20px] font-medium", children: /* @__PURE__ */ jsx(KatexText, { text: answer.answer }) })
              ]
            },
            answer.key
          );
        })
      }
    ),
    !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(
      SubmitButton,
      {
        onClick: handleSubmit,
        disabled: !canSubmit,
        label: submitLabel ?? (selectedKeys.length === 0 ? t("types.multiChoice.selectOne") : void 0)
      }
    ),
    isSolved && /* @__PURE__ */ jsx(
      QuizResultFeedback,
      {
        isCorrect: (solveResult == null ? void 0 : solveResult.correct) ?? false,
        show: isSolved,
        correctAnswer: shouldShowCorrectAnswers ? Array.from(resolvedCorrectKeySet).map((key) => {
          var _a;
          return (_a = sortedAnswers.find((a) => a.key === key)) == null ? void 0 : _a.answer;
        }).filter(Boolean).join(", ") : void 0,
        disableSound
      }
    ),
    isSolved && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: (solveResult == null ? void 0 : solveResult.description) ?? void 0,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: !(solveResult == null ? void 0 : solveResult.correct),
        actionLabel: isShowingCorrectAnswers ? t("common.viewMyAnswer") : t("common.viewCorrectAnswer"),
        onActionClick: () => setIsShowingCorrectAnswers((v) => !v)
      }
    )
  ] });
}
export {
  MultiChoiceStudent
};
//# sourceMappingURL=multi-choice-student.js.map
